import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../i18n';
import './MonthLedger.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const shiftMonth = (period, delta) => {
  if (!/^\d{4}-\d{2}$/.test(period || '')) return period; // cleared month input → no-op
  const [y, m] = period.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * "Të regjistruara" — the month's saved records for one scope category,
 * shown beside the entry form so users see what they've already registered
 * without leaving the screen. Fed by the same endpoint as the invoice
 * register; rows can be deleted in place (prefix-aware DELETE).
 */
export const MonthLedger = ({
  period,             // 'YYYY-MM' — follows the form; arrows browse other months
  recordTypes = [],   // e.g. ['scope1-stationary']
  buildings = [],     // [{id, name}] for row labels
  refreshKey = 0,     // bump after a save to refetch
  emptyHint,
  onChanged           // called after a row is deleted
}) => {
  const navigate = useNavigate();
  const { t, lang } = useT();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  // The month being browsed — re-synced to the form's period whenever it changes
  const [viewPeriod, setViewPeriod] = useState(period);
  useEffect(() => { setViewPeriod(period); }, [period]);

  // Guard against out-of-order responses when the user clicks ‹ › quickly:
  // only the latest request may write state.
  const reqSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_BASE}/api/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (seq !== reqSeq.current) return; // a newer month was requested meanwhile
      const filtered = (Array.isArray(data) ? data : [])
        .filter(r => r.type === 'emission_record' && recordTypes.includes(r.record_type) && r.period === viewPeriod)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setRows(filtered);
    } catch (err) {
      if (seq !== reqSeq.current) return;
      console.error('MonthLedger load failed:', err);
      setRows([]);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewPeriod, refreshKey, JSON.stringify(recordTypes)]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (row) => {
    if (!window.confirm(t('ledger.confirmDelete'))) return;
    try {
      setDeletingId(row.id);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/invoices/${row.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Delete failed:', err);
      alert(t('ledger.deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const buildingName = (id) => (buildings.find(b => String(b.id) === String(id)) || {}).name || '';
  const total = rows.reduce((s, r) => s + (parseFloat(r.emissions_tco2e) || 0), 0);

  // Strip the "(YYYY-MM)" suffix the register titles carry — the period is in the header
  const rowTitle = (r) => String(r.title || '').replace(/\s*\(\d{4}-\d{2}\)\s*$/, '');

  const monthLabel = (() => {
    if (!/^\d{4}-\d{2}$/.test(viewPeriod || '')) return viewPeriod || '';
    const m = lang === 'en'
      ? ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      : ['Janar', 'Shkurt', 'Mars', 'Prill', 'Maj', 'Qershor', 'Korrik', 'Gusht', 'Shtator', 'Tetor', 'Nëntor', 'Dhjetor'];
    return `${m[Number(viewPeriod.slice(5, 7)) - 1]} ${viewPeriod.slice(0, 4)}`;
  })();

  return (
    <div className="mledger">
      <div className="mledger-head">
        <div>
          <h3>{t('ledger.title')}</h3>
          <div className="mledger-monthnav">
            <button type="button" aria-label={t('ledger.prevMonth')} onClick={() => setViewPeriod(p => shiftMonth(p, -1))}>‹</button>
            <span className="mledger-period">{monthLabel}</span>
            <button type="button" aria-label={t('ledger.nextMonth')} onClick={() => setViewPeriod(p => shiftMonth(p, 1))}>›</button>
          </div>
        </div>
        <div className="mledger-total">
          <b>{total.toFixed(3)}</b>
          <span>t CO₂e</span>
        </div>
      </div>

      {loading ? (
        <div className="mledger-skeleton">
          <div className="sk" style={{ width: '82%' }} />
          <div className="sk" style={{ width: '64%' }} />
          <div className="sk" style={{ width: '73%' }} />
        </div>
      ) : rows.length === 0 ? (
        <div className="mledger-empty">
          <div className="mledger-empty-art" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="8" y1="13" x2="16" y2="13" />
              <line x1="8" y1="17" x2="13" y2="17" />
            </svg>
          </div>
          <p>{emptyHint || t('ledger.emptyHint')}</p>
        </div>
      ) : (
        <div className="mledger-rows">
          {rows.map(r => (
            <div className="mledger-row" key={r.id}>
              <div className="mledger-row-main">
                <span className="mledger-row-title">{rowTitle(r)}</span>
                <span className="mledger-row-sub">
                  {buildingName(r.building_id)}
                  {r.quantity ? ` · ${Number(r.quantity).toLocaleString()} ${r.unit || ''}` : ''}
                </span>
              </div>
              <div className="mledger-row-side">
                <span className={`mledger-src ${r.source === 'ocr' ? 'ocr' : ''}`}>
                  {r.source === 'ocr' ? 'E skanuar' : 'Manual'}
                </span>
                <b className="mledger-row-val">{Number(r.emissions_tco2e || 0).toFixed(3)}</b>
                <button
                  type="button"
                  className="mledger-del"
                  aria-label="Fshi regjistrimin"
                  disabled={deletingId === r.id}
                  onClick={() => handleDelete(r)}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <button type="button" className="mledger-all" onClick={() => navigate('/invoices-new')}>
        Shiko të gjitha faturat →
      </button>
    </div>
  );
};

export default MonthLedger;
